import {MaybePromise} from '@coolcolduk/typescript-util';
import {rangeNumber} from '@coolcolduk/util';
import {useEffect, useState} from 'react';

export interface UsePaginationOptions {
  /** Number of items per page. Defaults to 10 */
  pageSize?: number;
  /** Total number of items. If not set, assumes only 1 page worth of items */
  totalItems?: number;
  /** Initial page number (1-based). Defaults to 1 */
  initialPage?: number;

  /** function to run when page changes */
  handlePageChange?: (
    startIndex: number,
    endIndex: number,
    pageSize: number,
    currentPage: number,
  ) => MaybePromise<void>;
}

/**
 * A hook to manage pagination state and calculations
 * @param options - Pagination options
 * @returns An object with pagination state and handlers
 * @deprecated will move to @coolcolduk/ui
 */
export default function usePagination(options: UsePaginationOptions = {}) {
  const {pageSize = 10, totalItems, initialPage = 1, handlePageChange} = options;

  // Calculate total pages from totalItems and pageSize
  // When no totalItems is set, assume only 1 page worth of items
  const effectiveTotalItems = totalItems ?? pageSize;
  // Ensure at least 1 page (even if totalItems is 0)
  const totalPages = Math.max(1, Math.ceil(effectiveTotalItems / pageSize));

  // Store raw page number internally (can be any number, not clamped)
  // This allows setting a page before we know the total, and it will be preserved
  const [rawCurrentPage, setRawCurrentPage] = useState(initialPage);

  // Clamp the returned currentPage to valid range [1, totalPages]
  // This ensures the output is always valid, even if rawCurrentPage is outside the range
  const currentPage = rangeNumber(rawCurrentPage, 1, totalPages);

  // Calculate startIndex (0-based) and endIndex for the current page
  // For page 1 with pageSize 10: startIndex = 0, endIndex = 9
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize - 1;

  useEffect(() => {
    if (handlePageChange) {
      handlePageChange(startIndex, endIndex, pageSize, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const nextPage = () => {
    // Allow setting any page number internally, clamp will happen on output
    setRawCurrentPage((prev) => prev + 1);
  };

  const prevPage = () => {
    // Allow setting any page number internally, clamp will happen on output
    // When running prevPage on page 1, it will still be clamped to 1
    setRawCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const gotoPage = (page: number) => {
    // Allow setting any page number internally, clamp will happen on output
    setRawCurrentPage(page);
  };

  return {
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    gotoPage,
    startIndex,
    endIndex,
    isFirstPage: currentPage === 1,
    isLastPage: currentPage === totalPages,

    // parameters
    pageSize,
    totalItems,
    initialPage,
  };
}
